import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from 'config/supabase';

type VerificationState = 'loading' | 'success' | 'error' | 'canceled';

interface VerificationResult {
  state: VerificationState;
  hasOnboarding: boolean;
  nextStep: string;
  errorMessage: string;
}

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 2000;

export const usePaymentVerification = (): VerificationResult => {
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<VerificationState>('loading');
  const [hasOnboarding, setHasOnboarding] = useState(false);
  const [nextStep, setNextStep] = useState('/onboarding');
  const [errorMessage, setErrorMessage] = useState('');

  const verifyPaymentSession = useCallback(async (sessionId: string, retryCount = 0) => {
    try {
      const { data, error } = await supabase.functions.invoke('verify-payment-session', {
        body: { session_id: sessionId },
      });

      if (error) {
        throw error;
      }

      // Success - subscription confirmed in DB
      if (data.success) {
        setState('success');
        setHasOnboarding(data.has_onboarding || false);
        setNextStep(data.next_step || '/onboarding');
        return;
      }

      // Pending - webhook still processing
      if (data.pending && retryCount < MAX_RETRIES) {
        console.log(`Webhook pending, retrying in ${RETRY_DELAY_MS}ms (attempt ${retryCount + 1}/${MAX_RETRIES})`);
        setTimeout(() => {
          verifyPaymentSession(sessionId, retryCount + 1);
        }, RETRY_DELAY_MS);
        return;
      }

      // Failed after retries or other error
      setState('error');
      setErrorMessage(
        data.message ||
        'Payment successful, but verification is taking longer than expected. Please refresh the page or contact support.'
      );

    } catch (err: any) {
      console.error('Payment verification error:', err);
      setState('error');
      setErrorMessage(
        'We encountered an error verifying your payment. Please refresh the page or contact support.'
      );
    }
  }, []);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const error = searchParams.get('error');
    const canceled = searchParams.get('canceled');

    // Handle canceled checkout
    if (canceled === 'true') {
      setState('canceled');
      return;
    }

    // Handle error from Stripe
    if (error) {
      setState('error');
      setErrorMessage('We couldn\'t process your payment. Please try again.');
      return;
    }

    // Handle success with session verification
    if (sessionId) {
      verifyPaymentSession(sessionId);
    } else {
      // No params - redirect to paywall
      window.location.href = '/paywall';
    }
  }, [searchParams, verifyPaymentSession]);

  return {
    state,
    hasOnboarding,
    nextStep,
    errorMessage,
  };
};
